import { FC, useMemo } from "react";

import { DataGrid, GridColDef } from "@mui/x-data-grid";

import { getCollectionById } from "@/components/feedback/data/feedbackCollectionFixtures";
import { FeedbackResponseRow } from "@/components/feedback/types";

// EXTRACT: candidate for libs/ui — generic submissions DataGrid wrapper.
// Once extracted, columns should be passed in as a prop rather than hard-coded.

export interface SubmissionsTableProps {
  rows: FeedbackResponseRow[];
  rowCount: number;
  loading?: boolean;
  page: number;
  pageSize: number;
  onPaginationChange: (next: { page: number; pageSize: number }) => void;
  onRowClick: (row: FeedbackResponseRow) => void;
}

const formatDateTime = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
};

const SubmissionsTable: FC<SubmissionsTableProps> = ({
  rows,
  rowCount,
  loading = false,
  page,
  pageSize,
  onPaginationChange,
  onRowClick,
}) => {
  const columns = useMemo<GridColDef<FeedbackResponseRow>[]>(
    () => [
      { field: "id", headerName: "ID", width: 90 },
      {
        field: "feedback_collection_id",
        headerName: "Survey Name",
        width: 220,
        valueGetter: (value: string) => getCollectionById(value)?.name ?? value,
      },
      {
        field: "time_created",
        headerName: "Time Created",
        width: 200,
        valueFormatter: (value: string) => formatDateTime(value),
      },
      {
        field: "time_updated",
        headerName: "Time Updated",
        width: 200,
        valueFormatter: (value: string) => formatDateTime(value),
      },
      {
        field: "time_submitted",
        headerName: "Time Submitted",
        width: 200,
        valueFormatter: (value: string) => formatDateTime(value),
      },
    ],
    [],
  );

  return (
    <DataGrid<FeedbackResponseRow>
      rows={rows}
      columns={columns}
      loading={loading}
      rowCount={rowCount}
      paginationMode="server"
      paginationModel={{ page, pageSize }}
      onPaginationModelChange={onPaginationChange}
      pageSizeOptions={[10, 25, 50]}
      onRowClick={(params) => onRowClick(params.row)}
      disableRowSelectionOnClick
      sx={{
        "& .MuiDataGrid-row": { cursor: "pointer" },
      }}
    />
  );
};

export default SubmissionsTable;
